import { useRef, type ChangeEvent } from 'react';
import type { Profile } from '@wlb/shared/types';
import type { MediaFile } from '@wlb/shared/types/post';
import {
  POST_MEDIA_TYPE,
  SUPPORTED_VIDEO_MIME_TYPES,
} from '@wlb/shared/constants';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Avatar } from '../../components/ui/Avatar';
import {
  PostFeelingIcon,
  PostImageIcon,
  PostVideoIcon,
} from '../../components/ui/icons';
import { getProfileAvatarUrl } from '../profile/useCurrentProfile';
import type { CreatePostIntent } from '../createPost/createPostRoute';
import { getUsernameDisplay } from '../../utils/usernameDisplay';
import './feedPostComposer.css';

interface FeedPostComposerProps {
  profile: Profile | null;
  onOpenComposer: (intent: CreatePostIntent, initialMedia?: MediaFile) => void;
}

interface ComposerAction {
  intent: Exclude<CreatePostIntent, 'text'>;
  labelKey: string;
  className: string;
  Icon: typeof PostImageIcon;
}

const COMPOSER_ACTIONS: ComposerAction[] = [
  {
    intent: 'photo',
    labelKey: 'home.createPost.feedComposer.addPhoto',
    className: 'feed-post-composer__action--photo',
    Icon: PostImageIcon,
  },
  {
    intent: 'video',
    labelKey: 'home.createPost.feedComposer.addVideo',
    className: 'feed-post-composer__action--video',
    Icon: PostVideoIcon,
  },
  {
    intent: 'feeling',
    labelKey: 'home.createPost.feedComposer.addFeeling',
    className: 'feed-post-composer__action--feeling',
    Icon: PostFeelingIcon,
  },
];

export function FeedPostComposer({
  profile,
  onOpenComposer,
}: FeedPostComposerProps) {
  const { t } = useTranslation();
  const username = getUsernameDisplay(profile, t('home.rightBanner.guestName'));
  const videoInputRef = useRef<HTMLInputElement>(null);

  const handleAction = (intent: Exclude<CreatePostIntent, 'text'>) => {
    if (intent === 'photo') {
      onOpenComposer('photo');
      return;
    }
    if (intent === 'video') {
      videoInputRef.current?.click();
      return;
    }
    onOpenComposer(intent);
  };

  const handleVideoSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    onOpenComposer('video', {
      uri: URL.createObjectURL(file),
      file,
      fileName: file.name,
      fileSize: file.size,
      type: file.type,
      mediaType: POST_MEDIA_TYPE.VIDEO,
    });
  };

  return (
    <section
      className="feed-post-composer"
      aria-label={t('home.createPost.feedComposer.startPost')}
    >
      <Avatar
        src={getProfileAvatarUrl(profile)}
        username={username}
        seed={profile?.userGuid ?? profile?.userId}
        profileUserGuid={profile?.userGuid}
        userId={profile?.userId}
        canonical
        size="md"
        className="feed-post-composer__avatar"
      />
      <button
        type="button"
        className="feed-post-composer__prompt"
        onClick={() => onOpenComposer('text')}
      >
        {t('home.createPost.feedComposer.prompt')}
      </button>
      <div className="feed-post-composer__actions">
        {COMPOSER_ACTIONS.map(({ intent, labelKey, className, Icon }) => (
          <button
            key={labelKey}
            type="button"
            className={`feed-post-composer__action ${className}`}
            onClick={() => handleAction(intent)}
            aria-label={t(labelKey)}
          >
            <Icon size={22} />
          </button>
        ))}
      </div>
      <input
        ref={videoInputRef}
        className="feed-post-composer__file-input"
        type="file"
        tabIndex={-1}
        aria-hidden
        accept={SUPPORTED_VIDEO_MIME_TYPES.join(',')}
        onChange={handleVideoSelect}
      />
    </section>
  );
}
